import { cn } from "@/lib/styles.utils";
import {
  useContext,
  useId,
  type FC,
  type ReactNode,
  createContext,
} from "react";

type ModalSize = "sm" | "md" | "lg" | "xl";

interface ModalProps {
  children: ReactNode;
  size?: ModalSize;
}

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: "w-[360px] max-w-[90vw]",
  md: "w-[520px] max-w-[92vw]",
  lg: "w-[720px] max-w-[94vw]",
  xl: "w-[960px] max-w-[96vw]",
};

type Ctx = { labelId: string; descId: string; size: ModalSize };

const ModalCtx = createContext<Ctx | null>(null);

const useModalCtx = () => {
  const ctx = useContext(ModalCtx);
  if (!ctx) throw new Error("Modal subcomponent must be used within <Modal>");
  return ctx;
};

const Modal: FC<ModalProps> & {
  Header: FC<{ children: ReactNode; className?: string }>;
  Content: FC<{ children: ReactNode; className?: string }>;
  Footer: FC<{ children: ReactNode; className?: string }>;
} = ({ children, size = "xl" }) => {
  const labelId = useId();
  const descId = useId();

  return (
    <ModalCtx.Provider value={{ labelId, descId, size }}>
      <div
        data-testid="modal"
        className="fixed overflow-auto bg-ib-black bg-opacity-70 w-[100vw] min-w-[100vw] h-[100vh] min-h-[100vh] top-0 right-0 z-[1000]"
        aria-modal="true"
        role="dialog"
        aria-labelledby={labelId}
        aria-describedby={descId}
      >
        <div
          className={cn(
            "absolute top-[100px] right-1/2 transform translate-x-1/2 bg-ib-white w-fit p-6 rounded-[2px] shadow-modal-1",
            SIZE_CLASS[size],
          )}
        >
          {children}
        </div>
      </div>
    </ModalCtx.Provider>
  );
};

const ModalHeader: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  const { labelId } = useModalCtx();
  return (
    <div
      data-testid="answer-modal-header"
      id={labelId}
      className={cn("px-6 py-4", className || "")}
    >
      {children}
    </div>
  );
};

const ModalContent: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  const { descId } = useModalCtx();
  return (
    <div
      data-testid="answer-modal-content"
      id={descId}
      className={cn("px-6 py-4", className || "")}
    >
      {children}
    </div>
  );
};

const ModalFooter: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <div
    data-testid="answer-modal-footer"
    className={cn(
      "px-6 py-4 border-t border-gray-200 flex justify-end gap-3",
      className || "",
    )}
  >
    {children}
  </div>
);

Modal.Header = ModalHeader;
Modal.Content = ModalContent;
Modal.Footer = ModalFooter;

export { Modal };
